import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { desktopTheme as t } from '../../styles/desktopTheme';
import { timelineApi } from '../../utils/timelineApi';

const Wrap = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${t.textPrimary};
`;

const RightActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const SecondaryButton = styled.button`
  padding: 8px 12px;
  background: transparent;
  border: 1px solid ${t.borderSubtle};
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 11px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: ${t.borderStrong};
  }
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 18px;
`;

const FilterPill = styled.button`
  padding: 8px 10px;
  border-radius: 999px;
  border: 1px solid ${p => (p.$active ? t.borderStrong : t.borderSubtle)};
  background: ${p => (p.$active ? 'rgba(255,255,255,0.08)' : 'transparent')};
  color: ${p => (p.$active ? t.textPrimary : 'rgba(255,255,255,0.75)')};
  font-size: 11px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const Group = styled.div`
  margin-bottom: 18px;
`;

const GroupTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 10px;
`;

const ItemCard = styled.div`
  background: ${t.surface};
  border: 1px solid ${t.borderSubtle};
  border-radius: 14px;
  box-shadow: ${t.shadowCard};
  padding: 14px 14px;
  margin-bottom: 10px;
`;

const ItemTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

const ItemType = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  color: ${t.accent};
  text-transform: uppercase;
`;

const ItemTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${t.textPrimary};
  margin-top: 4px;
`;

const ItemMeta = styled.div`
  font-size: 10px;
  color: ${t.textTertiary};
  white-space: nowrap;
`;

const ItemDesc = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.45;
`;

const Empty = styled.div`
  padding: 40px 0;
  color: ${t.textTertiary};
  font-size: 12px;
`;

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'shows', label: 'Shows' },
  { id: 'releases', label: 'Releases' },
  { id: 'travel', label: 'Travel' },
  { id: 'tasks', label: 'Tasks' },
];

export default function DesktopTimeline() {
  const navigate = useNavigate();
  const [timeline, setTimeline] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const loadTimeline = async () => {
    setLoading(true);
    try {
      const response = await timelineApi.getTimeline();
      if (response && response.success) {
        setTimeline(response.timeline || []);
      } else {
        setTimeline([]);
      }
    } catch (e) {
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimeline();
  }, []);

  const filtered = useMemo(() => {
    let items = Array.isArray(timeline) ? [...timeline] : [];
    if (activeFilter !== 'all') {
      const filterMap = {
        shows: ['show', 'ticket-update'],
        releases: ['release'],
        travel: ['flight', 'hotel', 'transport'],
        tasks: ['task'],
      };
      const types = filterMap[activeFilter] || [];
      if (types.length) items = items.filter((i) => types.includes(i.type));
    }

    items.sort((a, b) => new Date(a.date) - new Date(b.date));

    const grouped = {};
    items.forEach((item) => {
      const key = item.dateGroup || 'Other';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(item);
    });
    return grouped;
  }, [timeline, activeFilter]);

  const groupKeys = useMemo(() => Object.keys(filtered), [filtered]);

  return (
    <Wrap>
      <HeaderRow>
        <Title>Timeline</Title>
        <RightActions>
          <SecondaryButton onClick={() => navigate('/timeline-settings')}>Settings</SecondaryButton>
          <SecondaryButton onClick={loadTimeline}>Refresh</SecondaryButton>
        </RightActions>
      </HeaderRow>

      <FiltersRow>
        {FILTERS.map((f) => (
          <FilterPill
            key={f.id}
            $active={activeFilter === f.id}
            onClick={() => setActiveFilter(f.id)}
          >
            {f.label}
          </FilterPill>
        ))}
      </FiltersRow>

      {loading ? <Empty>Loading…</Empty> : null}

      {!loading && groupKeys.length === 0 ? (
        <Empty>No upcoming items.</Empty>
      ) : (
        groupKeys.map((group) => (
          <Group key={group}>
            <GroupTitle>{group}</GroupTitle>
            {(filtered[group] || []).map((item) => (
              <ItemCard key={item._id || `${item.type}-${item.date}-${item.title}`}>
                <ItemTop>
                  <div>
                    <ItemType>{item.type || 'EVENT'}</ItemType>
                    <ItemTitle>{item.title || item.name || 'Untitled'}</ItemTitle>
                  </div>
                  <ItemMeta>
                    {item.date ? new Date(item.date).toLocaleString() : ''}
                  </ItemMeta>
                </ItemTop>
                {item.description ? <ItemDesc>{item.description}</ItemDesc> : null}
              </ItemCard>
            ))}
          </Group>
        ))
      )}
    </Wrap>
  );
}


